import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ScoreCard.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';

/**
 * ScoreCard Component
 * A centralized hover diagnostic popup that fetches and displays lead scoring details.
 * 
 * Props:
 * - leadId: string (Required)
 * - mobileNo: string (Optional fallback)
 * - personName: string (Optional fallback)
 * - children: ReactNode (The trigger element)
 */
const ScoreCard = ({ leadId, mobileNo, personName, initialScore, children }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [scoreData, setScoreData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const [popupCoords, setPopupCoords] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (isHovered && !scoreData && leadId && !isLoading) {
            fetchScoreDetails();
        }
    }, [isHovered, leadId]);


 const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const fetchScoreDetails = async () => {
        setIsLoading(true);
        try {
            const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
            const SCORECARD_URL = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_SCORE_CARD_DETAILS_FOR_EDIT}`.trim();

            const response = await fetch(SCORECARD_URL  , {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify({
                    Token: "-2295521862261168",
                    Details: {
                        EnquiryID: leadId ? leadId.toString() : ""
                    }
                })
            });

            if (response.ok) {
                const result = await response.json();
                const d = result.Details || result.d;
                if (Array.isArray(d) && d.length > 0) {
                    setScoreData(d[0]);
                } else {
                    setScoreData(d || {});
                }
            }
        } catch (error) {
            console.error("Error fetching scorecard:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            // Estimated dimensions for initial placement (width is 360px from CSS)
            const cardWidth = 360;
            const cardHeight = 280;

            let top = rect.bottom + scrollY;
            let left = rect.right + scrollX;

            // Initial check: if hitting bottom, flip up
            if (rect.bottom + cardHeight > viewportHeight && rect.top > cardHeight) {
                top = rect.top + scrollY - cardHeight + 20;
            }

            // Check if hitting right edge, flip left
            if (rect.right + cardWidth > viewportWidth) {
                left = rect.left + scrollX - cardWidth - 10;
            }

            setPopupCoords({ top, left });
        }
        setIsHovered(true);
    };

    // Precise adjustment after render to handle variable height
    useEffect(() => {
        if (isHovered && popupRef.current && triggerRef.current) {
            const popupRect = popupRef.current.getBoundingClientRect();
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const scrollY = window.scrollY;

            // If opening downwards but hits the bottom of the viewport
            if (popupCoords.top + popupRect.height > viewportHeight + scrollY) {
                setPopupCoords(prev => ({
                    ...prev,
                    top: triggerRect.top + scrollY - popupRect.height + 20
                }));
            }
        }
    }, [isHovered, scoreData]); // Recalculate when data loads or hover changes

    const handleMouseLeave = (e) => {
        // Delay hiding slightly to allow moving into the popup
        setTimeout(() => {
            if (popupRef.current && popupRef.current.matches(':hover')) {
                // If they moved into the popup, don't hide
                popupRef.current.addEventListener('mouseleave', () => {
                    setIsHovered(false);
                }, { once: true });
                return;
            }
            setIsHovered(false);
        }, 150);
    };

    return (
        <>
            <div
                ref={triggerRef}
                className="scorecard-wrapper"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
                <div className="clout-badge">
                    {scoreData?.CurrentScore ?? initialScore ?? 0}
                </div>

            </div>


            {isHovered && createPortal(
                <div
                    ref={popupRef}
                    className="scorecard-hover-popup-v2"
                    style={{
                        position: 'absolute',
                        top: popupCoords.top,
                        left: popupCoords.left,
                        marginTop: '-20px',
                        marginLeft: '10px'
                    }}
                >
                    <div className="scorecard-white-card-v2">
                        {isLoading && !scoreData ? (
                            <div className="scorecard-loader-container">
                                <div className="scorecard-mini-spinner"></div>
                            </div>
                        ) : scoreData ? (
                            <>
                                <div className="scorecard-header-v2">
                                    <h4 className="scorecard-mobile-v2">{mobileNo || personName || "Lead Details"}</h4>
                                    <p className="scorecard-since-v2">Lead since {scoreData.CreatedDate || scoreData.CreatedOn || 'N/A'}</p>
                                </div>
                                <div className="scorecard-summary-row-v2">
                                    <span className="summary-text-v2">
                                        Lead score <span className={`score-trend-v2 ${(scoreData.Profit_Loss || '').includes('Green') ? 'up' : 'down'}`}>
                                            {(scoreData.Profit_Loss || '').includes('Green') ? '▲' : '▼'}{scoreData.ScorePRCNTG || '0'}%
                                        </span> in last 7 days. Top scoring factor(s):
                                    </span>
                                </div>
                                <div className="scorecard-body-v2">
                                    <div className="score-circle-large-v2">
                                        <span className="score-val-v2">{scoreData.CurrentScore || '0'}</span>
                                    </div>
                                    <div className="score-factors-v-list-v2">
                                        {(scoreData.Top3ScoringFactors || "").split('#').filter(f => f).map((factor, i) => {
                                            const statuses = (scoreData.Top3_ProfileLoss || "").split('#');
                                            const status = statuses[i] || 'Green_Up';
                                            const isUp = status.toLowerCase().includes('up') || status.toLowerCase().includes('green');

                                            return (
                                                <div key={i} className="score-factor-row-v2">
                                                    <span className={`factor-arrow-v2 ${isUp ? 'up' : 'down'}`}>
                                                        {isUp ? '▲' : '▼'}
                                                    </span>
                                                    <div className="factor-desc-v2">{factor}</div>
                                                </div>
                                            );
                                        })}
                                        {!(scoreData.Top3ScoringFactors) && (
                                            <div className="score-factor-empty">No factors recorded</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="scorecard-error">Failed to load payload</div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ScoreCard;
